/**
 * Display helpers for OUR Telegram account that talks to a client (KAN-618).
 * Shared by the dialog bubble and the clients list so both name it the same.
 */

interface SenderAccountNameParts {
  profile_name?: string | null;
  persona_name?: string | null;
  label?: string | null;
  phone?: string | null;
}

/** KAN-871: mark of a manager's personal Telegram account (absent: company account). */
interface PersonalAccountMark {
  personal_manager_id?: string | null;
  personal_manager_name?: string | null;
}

/** KAN-931: messenger of our account; absent on an older backend: Telegram. */
export type MessengerChannel = 'telegram' | 'max';

/** KAN-931: channel of the account (absent: Telegram). */
export function senderAccountChannel(
  account: { channel?: string | null } | null | undefined
): MessengerChannel {
  return account?.channel === 'max' ? 'max' : 'telegram';
}

/**
 * KAN-931: key of a "send from" option: the account id for Telegram (as
 * before), "max:<id>" for MAX, so equal ids of two channels stay apart.
 */
export function senderOptionKey(channel: MessengerChannel, accountId: string): string {
  return channel === 'telegram' ? accountId : `${channel}:${accountId}`;
}

/**
 * Fields needed to show the account (name, username, avatar). Both the client
 * list account (ClientSenderAccount) and the message account
 * (MessageSenderAccount) fit this shape.
 */
export interface SenderAccountDisplay extends SenderAccountNameParts, PersonalAccountMark {
  username?: string | null;
  avatar_url?: string | null;
  /** KAN-931: messenger of the account; absent: Telegram. */
  channel?: MessengerChannel | null;
}

/** KAN-871: who looks at the feed: their manager id and own personal account. */
export interface SenderAccountViewer {
  /** Manager.id of the current user; null/absent: owner or operator (no manager). */
  managerId?: string | null;
  /** Own personal account from /auth/me (personal_telegram_account). */
  personalAccount?: {
    account_id: string;
    label?: string | null;
    username?: string | null;
  } | null;
}

/** KAN-871: the account is a manager's personal Telegram account. */
export function isPersonalSenderAccount(account: PersonalAccountMark | null | undefined): boolean {
  return !!(account?.personal_manager_id ?? '').trim();
}

/** KAN-871: a personal account of someone else (or any personal one without a manager). */
function isForeignPersonal(account: PersonalAccountMark, managerId: string | null | undefined): boolean {
  return isPersonalSenderAccount(account) && account.personal_manager_id !== (managerId || null);
}

/**
 * Account of the latest message that carries one (null when none).
 * KAN-871: with a viewer, personal accounts of other managers are skipped (the
 * viewer can not send from them); without it the latest account as before.
 */
export function latestSenderAccount<T extends SenderAccountDisplay>(
  messages: ReadonlyArray<{ sender_account?: T | null }> | undefined,
  viewer?: SenderAccountViewer
): T | null {
  if (!messages) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const account = messages[i].sender_account;
    if (!account) continue;
    if (viewer && isForeignPersonal(account, viewer.managerId)) continue;
    return account;
  }
  return null;
}

/** KAN-663: one of OUR accounts seen in the feed, with the estimate it last wrote into. */
export interface SenderAccountOption<T extends SenderAccountDisplay> {
  accountId: string;
  /** KAN-931: messenger the message goes through. */
  channel: MessengerChannel;
  /** KAN-931: unique key of the option (senderOptionKey), the select value. */
  key: string;
  account: T;
  /** Estimate of the latest message of this account (null when the feed has none). */
  estimateId: string | null;
  /** Text for the dropdown: the alias plus the id when aliases collide. */
  label: string;
}

/** KAN-871: own personal account offered even before anything was sent from it. */
export interface OwnPersonalSenderAccount {
  account_id: string;
  label: string;
  phone: string;
  username: string;
  persona_name: string;
  profile_name?: string;
  avatar_url?: string;
  personal_manager_id: string;
  personal_manager_name?: string;
  /** KAN-931: a personal account is always Telegram. */
  channel?: MessengerChannel;
}

/** KAN-871: "От себя (@username)" or "От себя". */
export function ownPersonalLabel(username?: string | null): string {
  const name = senderAccountUsername(username);
  return name ? `От себя (@${name})` : 'От себя';
}

/**
 * KAN-871: latest company (not personal) account of the feed, any role (an
 * incoming message names the account it came to); else the fallback (the
 * client card account) when it is a company one.
 */
function latestCompanyAccount<T extends SenderAccountDisplay & { account_id?: string | null }>(
  messages: ReadonlyArray<{ sender_account?: T | null }>,
  fallbackAccount: T | null
): T | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const account = messages[i].sender_account;
    if (account && (account.account_id ?? '').trim() && !isPersonalSenderAccount(account)) return account;
  }
  if (fallbackAccount && !isPersonalSenderAccount(fallbackAccount)) return fallbackAccount;
  return null;
}

/**
 * KAN-663: our accounts that wrote to the client (role=assistant messages
 * with a sender_account id), latest used first; the first is the default
 * of the "send from" dropdown. Each option remembers the estimate of that
 * account's latest message, so a manual message goes into its dialog.
 * KAN-871: personal accounts of other managers are never offered (without a
 * viewer no personal account at all); the viewer's own personal account is
 * "От себя (@username)" and, when nothing was sent from it yet, goes last
 * (the company account stays the default). With any personal option the
 * latest company account (feed or fallbackAccount) is offered as well.
 */
export function senderAccountOptions<
  T extends SenderAccountDisplay & { account_id?: string | null },
>(
  messages:
    | ReadonlyArray<{
        role: string;
        sender_account?: T | null;
        estimate_id?: string;
      }>
    | undefined,
  viewer: SenderAccountViewer = {},
  fallbackAccount: T | null = null
): SenderAccountOption<T | OwnPersonalSenderAccount>[] {
  const options: SenderAccountOption<T | OwnPersonalSenderAccount>[] = [];
  const managerId = viewer.managerId || null;
  const own = managerId ? (viewer.personalAccount ?? null) : null;
  const ownId = (own?.account_id ?? '').trim();
  const seen = new Set<string>();
  const list = messages ?? [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const message = list[i];
    const account = message.sender_account;
    const accountId = (account?.account_id ?? '').trim();
    if (message.role !== 'assistant' || !account || !accountId) continue;
    const channel = senderAccountChannel(account);
    const key = senderOptionKey(channel, accountId);
    if (seen.has(key)) continue;
    const isOwn = channel === 'telegram' && !!ownId && accountId === ownId;
    if (!isOwn && isForeignPersonal(account, managerId)) continue;
    seen.add(key);
    options.push({
      accountId,
      channel,
      key,
      account,
      estimateId: message.estimate_id ?? null,
      label: isOwn
        ? ownPersonalLabel(own?.username || account.username)
        : channelLabel(channel, senderAccountAlias(account) || accountId),
    });
  }
  // KAN-871 (M1): with a personal option the company account must stay a
  // choice too (the client wrote to it, the manager answered personally):
  // otherwise the only option is personal and the company one is unreachable.
  const hasPersonal = !!ownId || options.some((o) => isPersonalSenderAccount(o.account));
  const company = hasPersonal ? latestCompanyAccount(list, fallbackAccount) : null;
  const companyId = (company?.account_id ?? '').trim();
  const companyChannel = senderAccountChannel(company);
  const companyKey = senderOptionKey(companyChannel, companyId);
  if (company && companyId && !seen.has(companyKey)) {
    seen.add(companyKey);
    options.push({
      accountId: companyId,
      channel: companyChannel,
      key: companyKey,
      account: company,
      estimateId: null,
      label: channelLabel(companyChannel, senderAccountAlias(company) || companyId),
    });
  }
  const aliasCount = new Map<string, number>();
  for (const option of options) {
    aliasCount.set(option.label, (aliasCount.get(option.label) ?? 0) + 1);
  }
  for (const option of options) {
    if ((aliasCount.get(option.label) ?? 0) > 1) {
      option.label = `${option.label} (${option.accountId})`;
    }
  }
  const ownKey = senderOptionKey('telegram', ownId);
  if (own && ownId && managerId && !seen.has(ownKey)) {
    options.push({
      accountId: ownId,
      channel: 'telegram',
      key: ownKey,
      account: {
        account_id: ownId,
        label: own.label ?? '',
        phone: '',
        username: senderAccountUsername(own.username),
        persona_name: '',
        personal_manager_id: managerId,
      },
      estimateId: null,
      label: ownPersonalLabel(own.username),
    });
  }
  return options;
}

/**
 * KAN-931: messenger a manual message goes through: the option it is sent
 * from (chosen or the only one), else the account shown as "С аккаунта",
 * else the latest incoming message of the client (a MAX client without our
 * replies yet). Telegram when nothing says otherwise.
 */
export function manualSendChannel(
  sendOption: { channel: MessengerChannel } | null,
  options: ReadonlyArray<{ channel: MessengerChannel }>,
  shownAccount: { channel?: string | null } | null,
  messages: ReadonlyArray<{ role: string; source?: string | null }> | undefined
): MessengerChannel {
  if (sendOption) return sendOption.channel;
  if (options.length === 1) return options[0].channel;
  if (shownAccount) return senderAccountChannel(shownAccount);
  const list = messages ?? [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].role === 'user') return list[i].source === 'max' ? 'max' : 'telegram';
  }
  return 'telegram';
}

/** KAN-931: dropdown text of a MAX option names the channel; Telegram as before. */
function channelLabel(channel: MessengerChannel, alias: string): string {
  return channel === 'max' ? `MAX: ${alias}` : alias;
}

/** Name of the account: Telegram profile, then persona, label, phone. */
export function senderAccountName(account: SenderAccountNameParts): string {
  const candidates = [
    account.profile_name,
    account.persona_name,
    account.label,
    account.phone,
  ];
  for (const candidate of candidates) {
    const value = (candidate ?? '').trim();
    if (value) return value;
  }
  return '';
}

/** Username without the leading "@"; empty string when absent. */
export function senderAccountUsername(username?: string | null): string {
  return (username ?? '').trim().replace(/^@/, '');
}

/** First letters of the first two words, upper case; "?" when empty. */
export function nameInitials(name: string): string {
  const words = name.replace(/^@/, '').trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => Array.from(word)[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

/**
 * KAN-645: short alias of the account next to its avatar: the label from
 * Settings (e.g. "RU-2"), otherwise the first word of the profile name, then
 * persona, phone, @username.
 * KAN-871: a manager's personal account: "<ФИО менеджера>, личный".
 */
export function senderAccountAlias(
  account: SenderAccountDisplay & { account_id?: string | null }
): string {
  // KAN-871: a personal account is named by its manager: "ФИО, личный".
  if (isPersonalSenderAccount(account)) {
    const owner = (account.personal_manager_name ?? '').trim();
    if (owner) return `${owner}, личный`;
  }
  const label = (account.label ?? '').trim();
  if (label) return label;
  for (const candidate of [account.profile_name, account.persona_name]) {
    const word = (candidate ?? '').trim().split(/\s+/)[0];
    if (word) return word;
  }
  const phone = (account.phone ?? '').trim();
  if (phone) return phone;
  const username = senderAccountUsername(account.username);
  return username ? `@${username}` : (account.account_id ?? '').trim();
}

/** KAN-645: full name plus @username for the hover hint. */
export function senderAccountHint(account: SenderAccountDisplay): string {
  const name = senderAccountName(account);
  const username = senderAccountUsername(account.username);
  return [name, username && `@${username}`].filter(Boolean).join(' ');
}

/** KAN-645: Settings > Telegram with the account highlighted. */
export function senderAccountSettingsHref(accountId: string): string {
  return `/settings?tab=telegram&account=${encodeURIComponent(accountId)}`;
}
